import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, asistenciaService } from '../services/api';
import { formatDni } from '../utils/dni';
import ConfirmPopup from '../components/ConfirmPopup';
import './Asistencia.css';

const ROLES_ASISTENCIA = ['Profesor', 'Tutor', 'Pasante', 'Ayudante'];
const PER_PAGE = 30;

const formatDateShort = (isoDate) => {
  if (!isoDate) return '';
  // Parsear la fecha manualmente para evitar problemas con UTC
  // Si viene como "YYYY-MM-DD", parsear directamente sin usar Date que interpreta como UTC
  const dateStr = isoDate.split('T')[0]; // Obtener solo la parte de la fecha (YYYY-MM-DD)
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Los meses en JS son 0-indexed
    const day = parseInt(parts[2], 10);
    // Crear fecha en zona horaria local (no UTC)
    const d = new Date(year, month, day);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
  }
  // Fallback: usar el mismo truco que en MiAsistencia.js (agregar hora del mediodía)
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
};

const etiquetaAsistencia = (valor) => {
  if (valor === true) return 'Presente';
  if (valor === false) return 'Ausente';
  return 'Sin marcar';
};

const escapeCsv = (val) => {
  const s = String(val ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const Asistencia = () => {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [cursoSeleccionado, setCursoSeleccionado] = useState('');
  const [planilla, setPlanilla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPlanilla, setLoadingPlanilla] = useState(false);
  const [mostrarCursosInactivos, setMostrarCursosInactivos] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterAsistencia, setFilterAsistencia] = useState('todos'); // todos | presentes | ausentes
  const [fechaFiltro, setFechaFiltro] = useState('');
  const [exportAlcance, setExportAlcance] = useState('todos'); // todos | presentes | ausentes
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState('');
  const [sinAcceso, setSinAcceso] = useState(false);
  const [updatingManual, setUpdatingManual] = useState(false);
  const [updatingDias, setUpdatingDias] = useState(new Set());

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    const user = authService.getCurrentUser();
    const savedRole = localStorage.getItem('selectedRole');
    const rol = savedRole || (user?.groups && user.groups[0]);
    if (!rol || !ROLES_ASISTENCIA.includes(rol)) {
      setSinAcceso(true);
      setLoading(false);
      return;
    }
    loadCursos();
  }, [navigate]);

  const loadCursos = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await asistenciaService.getCursosAsistencia();
      setCursos(data || []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar cursos');
      setCursos([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cursoSeleccionado) {
      setPlanilla(null);
      return;
    }
    let cancelled = false;
    setLoadingPlanilla(true);
    setError('');
    asistenciaService.getPlanilla(cursoSeleccionado)
      .then((data) => {
        if (!cancelled) {
          setPlanilla(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Error al cargar la planilla');
          setPlanilla(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlanilla(false);
      });
    return () => { cancelled = true; };
  }, [cursoSeleccionado]);

  useEffect(() => {
    setFilterText('');
    setFilterAsistencia('todos');
    setFechaFiltro('');
    setExportAlcance('todos');
    setCurrentPage(1);
  }, [cursoSeleccionado]);

  useEffect(() => {
    const fechas = planilla?.fechas_clase || [];
    if (fechas.length === 0) {
      setFechaFiltro('');
      return;
    }
    if (!fechaFiltro || !fechas.includes(fechaFiltro)) {
      setFechaFiltro(fechas[fechas.length - 1]);
    }
  }, [planilla, fechaFiltro]);

  const cursosParaSelector = mostrarCursosInactivos
    ? (cursos || []).filter((c) => c.activo === false)
    : (cursos || []).filter((c) => c.activo !== false);

  useEffect(() => {
    const list = mostrarCursosInactivos ? (cursos || []).filter((c) => c.activo === false) : (cursos || []).filter((c) => c.activo !== false);
    if (cursoSeleccionado && !list.some((c) => String(c.id) === String(cursoSeleccionado))) {
      setCursoSeleccionado('');
    }
  }, [mostrarCursosInactivos, cursos, cursoSeleccionado]);

  const alumnos = planilla?.alumnos || [];
  const fechasClase = planilla?.fechas_clase || [];
  const asistencias = planilla?.asistencias || {};

  const esPresenteEnFecha = (alumnoId, fecha) => asistencias[`${alumnoId}_${fecha}`] === true;

  const presentesPorFecha = (() => {
    const counts = {};
    for (const fecha of fechasClase) {
      let total = 0;
      for (const alumno of alumnos) {
        if (esPresenteEnFecha(alumno.id, fecha)) total += 1;
      }
      counts[fecha] = total;
    }
    return counts;
  })();

  const filterLower = (filterText || '').trim().toLowerCase();
  const filteredAlumnos = alumnos.filter((a) => {
    if (filterLower) {
      const nombre = (a.nombre || '').toLowerCase();
      const email = (a.email || '').toLowerCase();
      if (!nombre.includes(filterLower) && !email.includes(filterLower)) return false;
    }
    if (filterAsistencia !== 'todos' && fechaFiltro) {
      const presente = esPresenteEnFecha(a.id, fechaFiltro);
      if (filterAsistencia === 'presentes' && !presente) return false;
      if (filterAsistencia === 'ausentes' && presente) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredAlumnos.length / PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * PER_PAGE;
  const paginatedAlumnos = filteredAlumnos.slice(start, start + PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, filterAsistencia, fechaFiltro]);

  const alumnosParaExportar = () => {
    if (exportAlcance === 'todos' || !fechaFiltro) return alumnos;
    if (exportAlcance === 'presentes') {
      return alumnos.filter((a) => esPresenteEnFecha(a.id, fechaFiltro));
    }
    return alumnos.filter((a) => !esPresenteEnFecha(a.id, fechaFiltro));
  };

  const exportarCsv = () => {
    const filasExport = alumnosParaExportar();
    if (filasExport.length === 0) {
      const msg =
        exportAlcance === 'presentes'
          ? 'No hay presentes para exportar en la fecha seleccionada.'
          : exportAlcance === 'ausentes'
            ? 'No hay ausentes para exportar en la fecha seleccionada.'
            : 'No hay alumnos para exportar.';
      setError(msg);
      return;
    }

    const cursoNombre = planilla?.curso?.nombre || 'curso';
    let header;
    let filas;

    if (exportAlcance === 'todos') {
      header = ['Alumno', 'DNI', 'Email', ...fechasClase.map((f) => formatDateShort(f) || f)].map(escapeCsv).join(',');
      filas = filasExport.map((alumno) => {
        const celdas = [
          escapeCsv(alumno.nombre || ''),
          escapeCsv(formatDni(alumno.dni || '')),
          escapeCsv(alumno.email || ''),
          ...fechasClase.map((fecha) => escapeCsv(etiquetaAsistencia(asistencias[`${alumno.id}_${fecha}`]))),
        ];
        return celdas.join(',');
      });
    } else {
      header = ['Alumno', 'DNI', 'Email', 'Fecha', 'Estado'].map(escapeCsv).join(',');
      filas = filasExport.map((alumno) =>
        [
          escapeCsv(alumno.nombre || ''),
          escapeCsv(formatDni(alumno.dni || '')),
          escapeCsv(alumno.email || ''),
          escapeCsv(formatDateShort(fechaFiltro) || fechaFiltro),
          escapeCsv(etiquetaAsistencia(asistencias[`${alumno.id}_${fechaFiltro}`])),
        ].join(',')
      );
    }

    const csv = `\uFEFF${header}\r\n${filas.join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slugCurso = String(cursoNombre).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'curso';
    const slugAlcance =
      exportAlcance === 'presentes' ? '-presentes' : exportAlcance === 'ausentes' ? '-ausentes' : '';
    const slugFecha =
      exportAlcance !== 'todos' && fechaFiltro ? `-${String(fechaFiltro).slice(0, 10)}` : '';
    a.download = `asistencia-${slugCurso}${slugAlcance}${slugFecha}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (sinAcceso) {
    return (
      <div className="asistencia-container">
        <div className="asistencia-sin-acceso">
          <h2>Sin acceso</h2>
          <p>Solo Profesor, Tutor, Pasante o Ayudante pueden ver la planilla de asistencia.</p>
          <button type="button" className="asistencia-btn asistencia-btn-secondary" onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="asistencia-container">
        <div className="asistencia-loading">
          <div className="asistencia-spinner" />
          <p>Cargando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="asistencia-container">
      <div className="asistencia-header">
        <h1>Planilla de Asistencia</h1>
        <div className="asistencia-curso-selector">
          <div className="asistencia-select-wrap">
            <label htmlFor="curso-asistencia">Curso</label>
            <select
              id="curso-asistencia"
              className="asistencia-select"
              value={cursoSeleccionado}
              onChange={(e) => setCursoSeleccionado(e.target.value)}
            >
              <option value="">Seleccionar curso</option>
              {cursosParaSelector.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.activo === false ? ' (inactivo)' : ''}
                </option>
              ))}
            </select>
          </div>
          <label className="asistencia-checkbox-inactivos">
            <input
              type="checkbox"
              checked={mostrarCursosInactivos}
              onChange={(e) => setMostrarCursosInactivos(e.target.checked)}
            />
            <span>Mostrar solo cursos inactivos</span>
          </label>
        </div>
      </div>

      {error && (
        <ConfirmPopup
          open
          title="Aviso"
          message={error}
          confirmLabel="Aceptar"
          cancelLabel={null}
          onConfirm={() => setError('')}
          onCancel={() => setError('')}
          variant="danger"
        />
      )}

      {loadingPlanilla && (
        <div className="asistencia-loading-planilla">
          <div className="asistencia-spinner" />
          <p>Cargando planilla...</p>
        </div>
      )}

      {planilla && !loadingPlanilla && (
        <div className="asistencia-planilla-wrap">
          <div className="asistencia-header-curso">
            <h2 className="asistencia-curso-titulo">{planilla.curso?.nombre}</h2>
            <div className="asistencia-manual-control">
              <label className="asistencia-checkbox-manual">
                <input
                  type="checkbox"
                  checked={planilla.curso?.asistencia_manual || false}
                  onChange={async (e) => {
                    setUpdatingManual(true);
                    try {
                      await asistenciaService.updateAsistenciaManual(cursoSeleccionado, e.target.checked);
                      const data = await asistenciaService.getPlanilla(cursoSeleccionado);
                      setPlanilla(data);
                    } catch (err) {
                      setError(err.response?.data?.error || 'Error al actualizar configuración');
                    } finally {
                      setUpdatingManual(false);
                    }
                  }}
                  disabled={updatingManual}
                />
                <span>Habilitar asistencia manual</span>
              </label>
            </div>
          </div>

          <div className="asistencia-toolbar">
            <div className="asistencia-filtro">
              <label htmlFor="asistencia-filtro-input" className="asistencia-filtro-label">
                Filtrar por nombre o email
              </label>
              <input
                id="asistencia-filtro-input"
                type="text"
                className="asistencia-filtro-input"
                placeholder="Escriba nombre o email..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>

            <div className="asistencia-filtro">
              <label htmlFor="asistencia-fecha-filtro" className="asistencia-filtro-label">
                Fecha
              </label>
              <select
                id="asistencia-fecha-filtro"
                className="asistencia-filtro-select"
                value={fechaFiltro}
                onChange={(e) => setFechaFiltro(e.target.value)}
                disabled={fechasClase.length === 0}
              >
                {fechasClase.length === 0 ? (
                  <option value="">Sin fechas</option>
                ) : (
                  fechasClase.map((fecha) => (
                    <option key={fecha} value={fecha}>
                      {formatDateShort(fecha)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="asistencia-filtro">
              <label htmlFor="asistencia-estado-filtro" className="asistencia-filtro-label">
                Asistencia
              </label>
              <select
                id="asistencia-estado-filtro"
                className="asistencia-filtro-select"
                value={filterAsistencia}
                onChange={(e) => setFilterAsistencia(e.target.value)}
                disabled={!fechaFiltro}
              >
                <option value="todos">Todos</option>
                <option value="presentes">Solo presentes</option>
                <option value="ausentes">Solo ausentes</option>
              </select>
            </div>

            <div className="asistencia-exportar">
              <label htmlFor="asistencia-export-alcance" className="asistencia-filtro-label">
                Exportar
              </label>
              <div className="asistencia-exportar-row">
                <select
                  id="asistencia-export-alcance"
                  className="asistencia-filtro-select"
                  value={exportAlcance}
                  onChange={(e) => setExportAlcance(e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="presentes">Solo presentes</option>
                  <option value="ausentes">Solo ausentes</option>
                </select>
                <button
                  type="button"
                  className="asistencia-btn-exportar"
                  onClick={exportarCsv}
                  disabled={alumnos.length === 0}
                  title={
                    exportAlcance === 'todos'
                      ? 'Exportar planilla completa'
                      : `Exportar ${exportAlcance} de la fecha seleccionada`
                  }
                >
                  📥 CSV
                </button>
              </div>
            </div>
          </div>

          <div className="asistencia-table-scroll">
            <table className="asistencia-table">
              <thead>
                <tr>
                  <th className="asistencia-col-alumno">Alumno</th>
                  {fechasClase.map((fecha) => {
                    const diaHabilitado = planilla.dias_manuales?.[fecha] === true;
                    const estaActualizando = updatingDias.has(fecha);
                    const esFechaFiltro = fecha === fechaFiltro && filterAsistencia !== 'todos';
                    return (
                      <th
                        key={fecha}
                        className={`asistencia-col-fecha${esFechaFiltro ? ' asistencia-col-fecha-filtro' : ''}`}
                        title={`${fecha} — ${presentesPorFecha[fecha] || 0} presente${(presentesPorFecha[fecha] || 0) === 1 ? '' : 's'}`}
                      >
                        <div className="asistencia-fecha-header">
                          <span className="asistencia-fecha-label">
                            {formatDateShort(fecha)}
                            <span className="asistencia-fecha-total" aria-label={`${presentesPorFecha[fecha] || 0} presentes`}>
                              ({presentesPorFecha[fecha] || 0})
                            </span>
                          </span>
                          {planilla.curso?.asistencia_manual && (
                            <label className="asistencia-checkbox-dia" title={diaHabilitado ? 'Día habilitado' : 'Día deshabilitado'}>
                              <input
                                type="checkbox"
                                checked={diaHabilitado}
                                onChange={async (e) => {
                                  setUpdatingDias(prev => new Set(prev).add(fecha));
                                  try {
                                    if (e.target.checked) {
                                      await asistenciaService.habilitarDia(cursoSeleccionado, fecha);
                                    } else {
                                      await asistenciaService.deshabilitarDia(cursoSeleccionado, fecha);
                                    }
                                    const data = await asistenciaService.getPlanilla(cursoSeleccionado);
                                    setPlanilla(data);
                                  } catch (err) {
                                    setError(err.response?.data?.error || 'Error al actualizar día');
                                  } finally {
                                    setUpdatingDias(prev => {
                                      const nuevo = new Set(prev);
                                      nuevo.delete(fecha);
                                      return nuevo;
                                    });
                                  }
                                }}
                                disabled={estaActualizando}
                              />
                            </label>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {alumnos.length === 0 ? (
                  <tr>
                    <td colSpan={fechasClase.length + 1} className="asistencia-empty">
                      No hay alumnos inscritos en este curso.
                    </td>
                  </tr>
                ) : paginatedAlumnos.length === 0 ? (
                  <tr>
                    <td colSpan={fechasClase.length + 1} className="asistencia-empty">
                      Ningún alumno coincide con el filtro.
                    </td>
                  </tr>
                ) : (
                  paginatedAlumnos.map((alumno) => (
                    <tr key={alumno.id}>
                      <td className="asistencia-col-alumno">
                        <span className="asistencia-alumno-nombre">{alumno.nombre}</span>
                        {alumno.email && <span className="asistencia-alumno-email">{alumno.email}</span>}
                      </td>
                      {fechasClase.map((fecha) => {
                        const key = `${alumno.id}_${fecha}`;
                        const presente = asistencias[key];
                        return (
                          <td key={fecha} className="asistencia-celda">
                            {presente === true && <span className="asistencia-presente">✓</span>}
                            {presente === false && <span className="asistencia-ausente">—</span>}
                            {(presente !== true && presente !== false) && <span className="asistencia-sin-marcar">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredAlumnos.length > 0 && (
            <div className="asistencia-paginacion">
              <button
                type="button"
                className="asistencia-btn-paginacion"
                disabled={page <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <span className="asistencia-paginacion-info">
                Mostrando {start + 1}-{Math.min(start + PER_PAGE, filteredAlumnos.length)} de {filteredAlumnos.length}
                {totalPages > 1 && ` · Página ${page} de ${totalPages}`}
              </span>
              <button
                type="button"
                className="asistencia-btn-paginacion"
                disabled={page >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          )}

          <p className="asistencia-leyenda">
            <span className="asistencia-leyenda-item"><span className="asistencia-presente">✓</span> Presente</span>
            <span className="asistencia-leyenda-item"><span className="asistencia-ausente">—</span> Ausente / Sin marcar</span>
          </p>
        </div>
      )}

      {!planilla && !loadingPlanilla && cursoSeleccionado && !error && (
        <div className="asistencia-sin-datos">
          <p>No se pudo cargar la planilla para este curso.</p>
        </div>
      )}

      {!cursoSeleccionado && !loading && cursos.length > 0 && (
        <div className="asistencia-instructions">
          <p>Seleccione un curso para ver la planilla de asistencia (alumnos y fechas de clase).</p>
        </div>
      )}

      {!loading && cursos.length === 0 && !error && (
        <div className="asistencia-sin-cursos">
          <p>No tiene cursos asignados para ver asistencia.</p>
        </div>
      )}
    </div>
  );
};

export default Asistencia;
